import {Button, Drawer, message, Space} from "antd";
import {CloseOutlined} from "@ant-design/icons";
import {useGetCustomerStoreServicesOrdersContext} from "../../../../../../../service-feature/useCases/get-orders/context/customer";
import {FormField, FormFieldError} from "../../../../../../../design-system/form-field";
import {Controller, FormProvider, useForm} from "react-hook-form";
import TextArea from "antd/lib/input/TextArea";
import {yupResolver} from "@hookform/resolvers/yup";
import yup from "../../../../../../../utils/yup-extended";
import {
  CustomerServiceOrderDeliverWorkFormEvidenceOfDeliveryUpload,
  FILE_LIMIT,
  hasAllowedExtension,
  MAX_FILE_SIZE,
} from "./evidence-of-delivery-upload";
import {useCreateCustomerStoreServiceOrderDisputeApi} from "../../../../../../../react-query-hooks/store/services/store-service-create-dispute";
import {StyledStoreServiceOrderDrawer} from "../styles";

export const storeServiceCustomerReviewRequestSchema = yup.object().shape({
  customerComment: yup.string().trim().required("Leave a comment"),
  evidenceOfDelivery: yup
    .array()
    .of(
      yup
        .mixed<File>()
        .test("is-file", "Invalid file", (value) => value instanceof File)
        .test("allowed-type", "Invalid file ext", (value) => {
          if (!value) return false;
          return hasAllowedExtension(value);
        })
        .test("file-size", "Each file must be under 5MB", (value) => {
          if (!value) return true;
          return value.size <= MAX_FILE_SIZE;
        })
        .test("debug", "Debug", (value) => {
          console.log("VALUE IN YUP:", value);
          return true;
        })
    )
    .min(1, "Upload a file")
    .max(FILE_LIMIT, `Max ${FILE_LIMIT} files allowed`)
    .required("Upload evidence of delivery")
    .default([]),
});

export function CustomerStoreServiceOrderOpenDisputeDrawer() {
  const {
    state: {selectedStoreServiceOrderInfo, activeStoreServiceOrderDrawer},
    dispatch,
  } = useGetCustomerStoreServicesOrdersContext();
  const createCustomerStoreServiceOrderDisputeApi =
    useCreateCustomerStoreServiceOrderDisputeApi();

  const formMethods = useForm<{
    customerComment: string;
    evidenceOfDelivery: File[];
  }>({
    resolver: yupResolver(storeServiceCustomerReviewRequestSchema),
    mode: "onSubmit",
    defaultValues: {
      customerComment: "",
    },
  });

  if (!selectedStoreServiceOrderInfo) return null; // or show a loader/skeleton
  const onOrderInfoDrawerClose = () => {
    formMethods.reset();
    dispatch({
      type: "set-active-store-service-order-drawer",
      payload: "order-info",
    });
  };
  const onSubmit = (values: {customerComment: string; evidenceOfDelivery: File[]}) => {
    // console.log({values});
    const formData = new FormData();

    // files as repeated "file"
    if (values.evidenceOfDelivery.length > 0) {
      formData.append("offering_type", "product-service");
      formData.append("resourceId", selectedStoreServiceOrderInfo.order.id.toString());
      formData.append("description", values.customerComment);
      values.evidenceOfDelivery.forEach((file) => {
        formData.append("file", file);
      });
      createCustomerStoreServiceOrderDisputeApi.mutate(
        {
          formData,
          resourceId: selectedStoreServiceOrderInfo.order.id.toString(),
          description: values.customerComment,
        },
        {
          onSuccess: () => {
            message.success("Your dispute has been submitted successfully");
            dispatch({
              type: "update-status-id",
              payload: 15,
            });
            onOrderInfoDrawerClose();
          },
        }
      );
    } else {
      message.error("Upload a file");
    }
  };
  return (
    <StyledStoreServiceOrderDrawer
      title={selectedStoreServiceOrderInfo.productService.name}
      placement="right"
      onClose={onOrderInfoDrawerClose}
      visible={activeStoreServiceOrderDrawer === "open-dispute"}
      closable={false}
      extra={
        <Button size="small" type="text" onClick={onOrderInfoDrawerClose}>
          <CloseOutlined />
        </Button>
      }
    >
      <div>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)} autoComplete="off">
            <Space size={24} direction="vertical" style={{display: "flex"}}>
              <FormField
                id="customerComment"
                label="Please tell us why you're disputing this order. Our team will review the case and get back to you."
                renderInput={(id) => (
                  <Controller
                    control={formMethods.control}
                    name="customerComment"
                    render={({
                      field: {onChange, value, ref, onBlur},
                      fieldState: {error},
                    }) => (
                      <Space size={8} direction="vertical" style={{display: "flex"}}>
                        <TextArea
                          id={id}
                          status={error?.message ? "error" : ""}
                          onChange={onChange}
                          onBlur={onBlur}
                          size="large"
                          value={value === null ? undefined : value}
                          placeholder="Briefly describe the issue"
                          ref={ref}
                        />
                        <FormFieldError errorMessage={error?.message} />
                      </Space>
                    )}
                  />
                )}
              />
              <CustomerServiceOrderDeliverWorkFormEvidenceOfDeliveryUpload />
              <div>
                <Button
                  disabled={
                    Object.keys(formMethods.formState.errors).length > 0 ||
                    Object.values(formMethods.watch()).length < 1
                  }
                  loading={createCustomerStoreServiceOrderDisputeApi.isLoading}
                  htmlType="submit"
                  size="small"
                  type="primary"
                >
                  Submit dispute
                </Button>
              </div>
            </Space>
          </form>
        </FormProvider>
      </div>
    </StyledStoreServiceOrderDrawer>
  );
}
